import { supabase } from '../lib/supabase';

export class TakeoffService {
  static transformTakeoffData(takeoff) {
    return {
      id: takeoff.id,
      projectName: takeoff.project_name,
      jobNumber: takeoff.job_number,
      status: takeoff.status,
      ufredoPrice: takeoff.ufredo_price,
      abCivilPrice: takeoff.ab_civil_price,
      completionNeededDate: takeoff.completion_needed_date,
      clientId: takeoff.client_id,
      contactId: takeoff.contact_id,
      email: takeoff.email,
      formPhone: takeoff.form_phone,
      formName: takeoff.form_name,
      otherRequest: takeoff.other_request,
      scopesNeeded: takeoff.scopes_needed,
      topSoilReplacementDepth: takeoff.top_soil_replacement_depth,
      topSoilStrippingDepth: takeoff.top_soil_stripping_depth,
      cadFileAvailable: takeoff.cad_file_available,
      initialAcceptance: takeoff.initial_acceptance,
      formClient: takeoff.form_client,
      invoicedStatus: takeoff.invoiced_status,
      earthworksTechInvNumber: takeoff.earthworks_tech_inv_number,
      poNumber: takeoff.po_number,
      invoicedDate: takeoff.invoiced_date,
      paidDate: takeoff.paid_date,
      paidToUfredo: takeoff.paid_to_ufredo,
      paidToUfredoDate: takeoff.paid_to_ufredo_date,
      createdAt: takeoff.created_at,
      updatedAt: takeoff.updated_at,
      airtableClientRecordId: takeoff.airtable_client_record_id,
      airtableCreatedDate: takeoff.airtable_created_date,
      fileAttachments: takeoff.file_attachments || []
    };
  }

  static transformTakeoffForDb(takeoff) {
    return {
      project_name: takeoff.projectName || takeoff.project_name,
      job_number: takeoff.jobNumber || takeoff.job_number,
      status: takeoff.status,
      ufredo_price: takeoff.ufredoPrice || takeoff.ufredo_price,
      ab_civil_price: takeoff.abCivilPrice || takeoff.ab_civil_price,
      completion_needed_date: takeoff.completionNeededDate || takeoff.completion_needed_date,
      client_id: takeoff.clientId || takeoff.client_id,
      contact_id: takeoff.contactId || takeoff.contact_id,
      email: takeoff.email,
      form_phone: takeoff.formPhone || takeoff.form_phone,
      form_name: takeoff.formName || takeoff.form_name,
      other_request: takeoff.otherRequest || takeoff.other_request,
      scopes_needed: takeoff.scopesNeeded || takeoff.scopes_needed,
      top_soil_replacement_depth: takeoff.topSoilReplacementDepth || takeoff.top_soil_replacement_depth,
      top_soil_stripping_depth: takeoff.topSoilStrippingDepth || takeoff.top_soil_stripping_depth,
      cad_file_available: takeoff.cadFileAvailable || takeoff.cad_file_available,
      initial_acceptance: takeoff.initialAcceptance || takeoff.initial_acceptance,
      form_client: takeoff.formClient || takeoff.form_client,
      invoiced_status: takeoff.invoicedStatus || takeoff.invoiced_status,
      earthworks_tech_inv_number: takeoff.earthworksTechInvNumber || takeoff.earthworks_tech_inv_number,
      po_number: takeoff.poNumber || takeoff.po_number,
      invoiced_date: takeoff.invoicedDate || takeoff.invoiced_date,
      paid_date: takeoff.paidDate || takeoff.paid_date,
      paid_to_ufredo: takeoff.paidToUfredo || takeoff.paid_to_ufredo,
      paid_to_ufredo_date: takeoff.paidToUfredoDate || takeoff.paid_to_ufredo_date,
      file_attachments: takeoff.fileAttachments || takeoff.file_attachments || []
    };
  }

  static async getAllTakeoffs() {
    try {
      const { data, error } = await supabase
        .from('takeoff_projects')
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            client_id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching takeoffs:', error);
        throw error;
      }

      return data?.map(takeoff => ({
        ...this.transformTakeoffData(takeoff),
        client: takeoff.clients,
        contact: takeoff.contacts
      })) || [];
    } catch (error) {
      console.error('Error in getAllTakeoffs:', error);
      throw new Error(`Failed to fetch takeoffs: ${error.message}`);
    }
  }

  static async getTakeoffById(id) {
    try {
      const { data, error } = await supabase
        .from('takeoff_projects')
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            client_id
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Supabase error fetching takeoff:', error);
        throw error;
      }

      return {
        ...this.transformTakeoffData(data),
        client: data.clients,
        contact: data.contacts
      };
    } catch (error) {
      console.error('Error in getTakeoffById:', error);
      throw new Error(`Failed to fetch takeoff: ${error.message}`);
    }
  }

  static async createTakeoff(takeoffData) {
    try {
      const dbTakeoff = this.transformTakeoffForDb(takeoffData);
      
      const { data, error } = await supabase
        .from('takeoff_projects')
        .insert([dbTakeoff])
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            client_id
          )
        `)
        .single();

      if (error) {
        console.error('Supabase error creating takeoff:', error);
        throw error;
      }

      return {
        ...this.transformTakeoffData(data),
        client: data.clients,
        contact: data.contacts
      };
    } catch (error) {
      console.error('Error in createTakeoff:', error);
      throw new Error(`Failed to create takeoff: ${error.message}`);
    }
  }

  static async updateTakeoff(id, takeoffData) {
    try {
      const dbTakeoff = this.transformTakeoffForDb(takeoffData);
      
      const { data, error } = await supabase
        .from('takeoff_projects')
        .update({
          ...dbTakeoff,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            client_id
          )
        `)
        .single();

      if (error) {
        console.error('Supabase error updating takeoff:', error);
        throw error;
      }

      return {
        ...this.transformTakeoffData(data),
        client: data.clients,
        contact: data.contacts
      };
    } catch (error) {
      console.error('Error in updateTakeoff:', error);
      throw new Error(`Failed to update takeoff: ${error.message}`);
    }
  }

  static async deleteTakeoff(id) {
    try {
      const { error } = await supabase
        .from('takeoff_projects')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase error deleting takeoff:', error);
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteTakeoff:', error);
      throw new Error(`Failed to delete takeoff: ${error.message}`);
    }
  }

  // Get clients for dropdown selection
  static async getClients() {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) {
        console.error('Supabase error fetching clients:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClients:', error);
      throw new Error(`Failed to fetch clients: ${error.message}`);
    }
  }

  // Get unique statuses for filtering
  static async getStatuses() {
    try {
      const { data, error } = await supabase
        .from('takeoff_projects')
        .select('status')
        .not('status', 'is', null)
        .not('status', 'eq', '');

      if (error) {
        console.error('Supabase error fetching statuses:', error);
        throw error;
      }

      // Extract unique statuses
      const uniqueStatuses = [...new Set(data.map(item => item.status))];
      return uniqueStatuses.filter(Boolean).sort();
    } catch (error) {
      console.error('Error in getStatuses:', error);
      throw new Error(`Failed to fetch statuses: ${error.message}`);
    }
  }

  // Get contacts for dropdown selection
  static async getContacts() {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          id,
          name,
          client_id,
          clients (
            id,
            name
          )
        `)
        .order('name', { ascending: true });

      if (error) {
        console.error('Supabase error fetching contacts:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getContacts:', error);
      throw new Error(`Failed to fetch contacts: ${error.message}`);
    }
  }

  // Get paginated takeoffs with server-side search, sort, and filters
  static async getPaginatedTakeoffs(page = 1, pageSize = 50, sortField = "jobNumber", sortDirection = "desc", searchQuery = "", filters = {}) {
    try {
      const offset = (page - 1) * pageSize;

      // Build the query with optional search
      let countQuery = supabase.from("takeoff_projects").select("*", { count: "exact", head: true });
      let dataQuery = supabase
        .from("takeoff_projects")
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            client_id
          )
        `);

      // Apply search filters if search query is provided
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim();

        // Apply search to count query (only text fields - job_number is integer and can't use ilike)
        countQuery = countQuery.or(
          `project_name.ilike.%${query}%,email.ilike.%${query}%`
        );

        // Apply search to data query (only text fields - job_number is integer and can't use ilike)
        dataQuery = dataQuery.or(
          `project_name.ilike.%${query}%,email.ilike.%${query}%`
        );
      }

      // Apply filters
      if (filters.status && filters.status !== '') {
        countQuery = countQuery.eq('status', filters.status);
        dataQuery = dataQuery.eq('status', filters.status);
      }

      if (filters.cadFileAvailable && filters.cadFileAvailable !== '') {
        countQuery = countQuery.eq('cad_file_available', filters.cadFileAvailable);
        dataQuery = dataQuery.eq('cad_file_available', filters.cadFileAvailable);
      }

      if (filters.invoicedStatus && filters.invoicedStatus !== '') {
        countQuery = countQuery.eq('invoiced_status', filters.invoicedStatus);
        dataQuery = dataQuery.eq('invoiced_status', filters.invoicedStatus);
      }

      // Get total count for pagination info
      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error('Count query error:', countError);
        throw new Error(`Failed to get takeoffs count: ${countError.message}`);
      }

      // Handle sorting - map UI field names to database field names
      let actualSortField = sortField;
      const fieldMapping = {
        "projectName": "project_name",
        "jobNumber": "job_number",
        "completionNeededDate": "completion_needed_date",
        "clientName": "clients(name)",
        "contactName": "contacts(name)",
        "ufredoPrice": "ufredo_price",
        "abCivilPrice": "ab_civil_price",
        "formPhone": "form_phone",
        "formName": "form_name",
        "cadFileAvailable": "cad_file_available"
      };

      // Apply field mapping if it exists
      if (fieldMapping[sortField]) {
        actualSortField = fieldMapping[sortField];
      }

      // Get paginated data with sorting and range
      const { data, error } = await dataQuery
        .order(actualSortField, { ascending: sortDirection === "asc" })
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Data query error:', error);
        throw new Error(`Failed to fetch takeoffs: ${error.message}`);
      }

      const processedData = data.map(takeoff => ({
        ...this.transformTakeoffData(takeoff),
        client: takeoff.clients,
        contact: takeoff.contacts,
        clientName: takeoff.clients?.name || 'N/A',
        contactName: takeoff.contacts?.name || 'N/A'
      }));

      return {
        data: processedData,
        totalCount: count,
        totalPages: Math.ceil(count / pageSize),
        currentPage: page,
        pageSize: pageSize,
        hasNextPage: page < Math.ceil(count / pageSize),
        hasPreviousPage: page > 1
      };
    } catch (error) {
      console.error('Error in getPaginatedTakeoffs:', error);
      throw error;
    }
  }
}